import { useAuth0 } from "@auth0/auth0-react";
import {
  AccountCircleOutlined,
  AnchorOutlined,
  ShoppingBagOutlined,
} from "@mui/icons-material";
import { Box, CircularProgress } from "@mui/material";
import {
  AccessControlBindings,
  Authenticated,
  CanAccess,
  DataProvider,
  ErrorComponent,
  LogicalFilter,
  Refine,
} from "@refinedev/core";
import {
  RefineSnackbarProvider,
  ThemedLayout,
  useNotificationProvider,
} from "@refinedev/mui";
import routerProvider from "@refinedev/react-router";
import { Navigate, Outlet, Route, Routes } from "react-router";
import { Header } from "./components/header";
import { Title } from "./components/title";
import { DashboardPage } from "./pages/dashboard";
import { Login } from "./pages/login";
import { EditOrder } from "./pages/orders/edit";
import { ListOrders } from "./pages/orders/list";
import { CreatePort } from "./pages/ports/create";
import { EditPort } from "./pages/ports/edit";
import { ListPorts } from "./pages/ports/list";
import { Unauthorized } from "./pages/unauthorized";
import { EditUser } from "./pages/users/edit";
import { ListUsers } from "./pages/users/list";
import { RequiredAuthProvider } from "./types/RequiredAuthProvider";
import { useState } from "react";
import { LogoutConfirmDialog } from "./components/LogoutConfirmDialog";

function App() {
  const {
    isAuthenticated,
    isLoading,
    user,
    logout,
    getIdTokenClaims,
    getAccessTokenSilently,
  } = useAuth0();

  const [logoutOpen, setLogoutOpen] = useState(false);

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          height: "100vh",
          width: "100vw",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress size={75} />
      </Box>
    );
  }

  const confirmLogout = () => {
    logout({ logoutParams: { returnTo: window.location.origin } });
    setLogoutOpen(false);
  };

  const authProvider: RequiredAuthProvider = {
    login: async () => {
      return {
        success: true,
      };
    },
    logout: async () => {
      setLogoutOpen(true);

      return {
        success: true
      }
      /*logout({ logoutParams: { returnTo: window.location.origin } });
      return {
        success: true,
      };*/
    },
    onError: async (error) => {
      if (error.response?.status === 401) {
        return {
          logout: true,
        };
      }

      return { error };
    },
    check: async () => {
      try {
        const token = await getIdTokenClaims();
        if (token) {
          // TODO:
          /*axios.defaults.headers.common = {
            Authorization: `Bearer ${token.__raw}`,
          };*/
          return {
            authenticated: true,
          };
        }
        return {
          authenticated: false,
          error: {
            message: "Check failed",
            name: "Token not found",
          },
          redirectTo: "/login",
          logout: true,
        };
      } catch (error: unknown) {
        return {
          authenticated: false,
          error: error instanceof Error ? error : new Error(String(error)),
          redirectTo: "/login",
          logout: true,
        };
      }
    },
    getPermissions: async () => {
      if (!isAuthenticated) return { roles: [] };

      const claims = await getIdTokenClaims();
      const roleKey =
        claims && Object.keys(claims).find((key) => key.includes("role"));

      return {
        roles: roleKey ? claims[roleKey] : [],
      };
    },
    getIdentity: async () => {
      if (user) {
        return {
          ...user,
          avatar: user.picture,
        };
      }
      return null;
    },
  };

  const ODATA_API_URL = `${import.meta.env.VITE_API_URL}/api/odata`;

  const dataProvider: DataProvider = {
    getOne: async ({ resource, id }) => {
      const token = await getAccessTokenSilently();
      const response = await fetch(`${ODATA_API_URL}/${resource}/${id}`, {
        headers: {
          Authorization: "Bearer " + token,
        },
      });

      if (!response.ok) throw response;

      const data = await response.json();

      return { data };
    },
    update: async ({ resource, id, variables }) => {
      const token = await getAccessTokenSilently();

      const response = await fetch(
        `${ODATA_API_URL}/${resource}/${id}/Metadata.SetMultipleEntry`,
        {
          headers: {
            Authorization: "Bearer " + token,
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          method: "POST",
          body: JSON.stringify(variables),
        }
      );

      const data = await response.json();

      return { data };
    },
    getList: async ({ resource, pagination, sorters, filters }) => {
      const token = await getAccessTokenSilently();

      // Handle pagination - new API structure  
      const current = (pagination as any)?.current ?? 1; // eslint-disable-line
      const pageSize = (pagination as any)?.pageSize ?? 10; // eslint-disable-line
      const skip = (current - 1) * pageSize;

      const query = new URLSearchParams({
        $top: String(pageSize),
        $skip: String(skip),
        $count: "true",
      });

      if (Array.isArray(sorters) && sorters.length > 0) {
        const orderBy = sorters
          .filter((item) => item.order !== undefined)
          .map(({ field, order }) => `${field} ${order}`)
          .join(",");
        if (orderBy) {
          query.set("$orderby", orderBy);
        }
      }

      function formatValue(value: unknown): string {
        if (typeof value === "string") {
          return `'${value.replace(/'/g, "''")}'`; // Escape single quotes
        }
        if (value instanceof Date) {
          return value.toISOString();
        }
        return String(value);
      }

      if (Array.isArray(filters) && filters.length > 0) {
        const filterStrings = filters
          .filter((staff): staff is LogicalFilter => true)
          .map(({ field, operator, value }) => {
            switch (operator) {
              case "eq":
                return `${field} eq ${formatValue(value)}`;
              case "ne":
                return `${field} ne ${formatValue(value)}`;
              case "lt":
                return `${field} lt ${formatValue(value)}`;
              case "gt":
                return `${field} gt ${formatValue(value)}`;
              case "lte":
                return `${field} le ${formatValue(value)}`;
              case "gte":
                return `${field} ge ${formatValue(value)}`;
              case "contains":
                return `contains(${field}, ${formatValue(value)})`;
              case "startswith":
                return `startswith(${field}, ${formatValue(value)})`;
              case "endswith":
                return `endswith(${field}, ${formatValue(value)})`;
              default:
                return "";
            }
          })
          .filter(Boolean);

        if (filterStrings.length > 0) {
          query.set("$filter", filterStrings.join(" or "));
        }
      }

      const response = await fetch(`${ODATA_API_URL}/${resource}?${query}`, {
        headers: {
          Authorization: "Bearer " + token,
        },
      });

      if (!response.ok) throw response;

      const data = await response.json();

      return {
        data: data.value,
        total: data["@odata.count"] ?? data.value.length,
      };
    },
    create: async ({ resource, variables }) => {
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: import.meta.env.VITE_AUTH0_AUDIENCE_BASE_URL,
          scope: "openid profile email",
        },
      });

      const response = await fetch(`${ODATA_API_URL}/${resource}`, {
        method: "POST",
        headers: {
          Authorization: "Bearer " + token,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(variables),
      });

      if (!response.ok) throw await response.json();

      return {
        data: await response.json(),
      };
    },
    deleteOne: () => {
      throw new Error("Not implemented");
    },
    custom: async ({ url, method, payload }) => {
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: import.meta.env.VITE_AUTH0_AUDIENCE_BASE_URL,
          scope: "openid profile email",
        },
      });
      const response = await fetch(url, {
        method,
        headers: {
          Authorization: "Bearer " + token,
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: payload ? JSON.stringify(payload) : undefined,
      });

      if (!response.ok) throw response;
      const data = await response.json();;

      return {
        data,
      };
    },
    getApiUrl: () => ODATA_API_URL,
  };

  const accessControlProvider: AccessControlBindings = {
    can: async () => {
      const permissions = (await authProvider.getPermissions?.()) ?? {
        roles: [],
      };
      const roles = permissions.roles || [];

      const isAdmin = roles.some((x) => x.toLowerCase() == "admin");

      return { can: isAdmin };
    },
  };

  
 
  return (
    <RefineSnackbarProvider>
      <Refine
        dataProvider={dataProvider}
        authProvider={authProvider}
        routerProvider={routerProvider}
        accessControlProvider={accessControlProvider}
        notificationProvider={useNotificationProvider}
        resources={[
          {
            name: "users",
            list: "/users",
            edit: "/users/:id/edit",
            meta: {
              label: "Users",
              icon: <AccountCircleOutlined />,
            },
          },
          {
            name: "orders",
            list: "/orders",
            edit: "/orders/:id/edit",
            meta: {
              label: "Orders",
              icon: <ShoppingBagOutlined />,
            },
          },
          {
            name: "ports",
            list: "/ports",
            edit: "/ports/:id/edit",
            create: "/ports/new",
            meta: {
              label: "Ports",
              icon: <AnchorOutlined />,
            },
          },
        ]}
      >
        <Routes>
          <Route
            element={
              <Authenticated
                key="authenticated"
                fallback={<Navigate to="/login" />}
              >
                <Outlet />
              </Authenticated>
            }
          >
            <Route
              element={
                <CanAccess fallback={<Navigate to="/unauthorized" />}>
                  <Outlet />
                </CanAccess>
              }
            >
              <Route
                element={
                  <ThemedLayout Header={Header} Title={Title}>
                    <Outlet />
                  </ThemedLayout>
                }
              >
                <Route index element={<DashboardPage />} />
                <Route path="users">
                  <Route index element={<ListUsers />} />
                  <Route path=":id/edit" element={<EditUser />} />
                </Route>
                <Route path="orders">
                  <Route index element={<ListOrders />} />
                  <Route path=":id/edit" element={<EditOrder />} />
                </Route>
                <Route path="ports">
                  <Route index element={<ListPorts />} />
                  <Route path="new" element={<CreatePort />} />
                  <Route path=":id/edit" element={<EditPort />} />
                </Route>
              </Route>
            </Route>
          </Route>
          <Route
            path="/unauthorized"
            element={
              <CanAccess fallback={<Unauthorized />}>
                <Navigate to="/" />
              </CanAccess>
            }
          />
          <Route
            element={
              <Authenticated key="unauthenticated" fallback={<Outlet />}>
                <Navigate to="/" />
              </Authenticated>
            }
          >
            <Route path="/login" element={<Login />} />
          </Route>
          <Route path="*" element={<ErrorComponent />} />
        </Routes>
        <LogoutConfirmDialog
          open={logoutOpen}
          onCancel={() => setLogoutOpen(false)}
          onConfirm={confirmLogout}
        />
      </Refine>
    </RefineSnackbarProvider>
  );
}

export default App;
